import type { ComparisonMetricKey } from './savedComparisonViews';

export type ComparisonMetricFormat = 'integer' | 'currency' | 'percent';

/**
 * Mapa métrica de UI → campo de `getClinicsComparison` (GET /metrics/compare).
 *
 * | UI key            | Label          | Campo API                         | Ranking server |
 * |-------------------|----------------|-----------------------------------|----------------|
 * | cajaIngresos      | Ingresos       | clinics[].caja.ingresos           | rankings.ingresos |
 * | turnosTotal       | Turnos         | clinics[].turnos.total            | — |
 * | turnosNoAsistio   | Ausentismo     | clinics[].turnos.noAsistio        | — |
 * | turnosAtendidos   | Atendidos      | clinics[].turnos.atendidos        | rankings.atendidos |
 * | pacientesNuevos   | Pacientes nuevos | clinics[].pacientes.nuevos      | rankings.pacientesNuevos |
 * | attendanceRate    | Asistencia     | clinics[].turnos.attendanceRate   | rankings.attendanceRate |
 *
 * No listar: PVP, ocupación, LTV, etc. — no vienen en compare. Pedir extensión a API.
 * Keys extra (`cajaNeto`, `pacientesActivos`) existen en la fila API y se aceptan
 * en vistas guardadas, pero no van al selector corto.
 */
export interface ComparisonMetricDef {
  key: ComparisonMetricKey;
  labelKey: string;
  /** Path documental sobre ComparisonClinicRow. */
  apiField: string;
  format: ComparisonMetricFormat;
  /** Si true, aparece en el selector de hasta 3. */
  inSelector: boolean;
}

export const COMPARISON_METRICS_CATALOG: readonly ComparisonMetricDef[] = [
  { key: 'cajaIngresos', labelKey: 'metrics.comparison.kpi.ingresos', apiField: 'caja.ingresos', format: 'currency', inSelector: true },
  { key: 'turnosTotal', labelKey: 'metrics.comparison.kpi.turnos', apiField: 'turnos.total', format: 'integer', inSelector: true },
  { key: 'turnosNoAsistio', labelKey: 'metrics.comparison.kpi.ausentismo', apiField: 'turnos.noAsistio', format: 'integer', inSelector: true },
  { key: 'turnosAtendidos', labelKey: 'metrics.comparison.kpi.atendidos', apiField: 'turnos.atendidos', format: 'integer', inSelector: true },
  { key: 'pacientesNuevos', labelKey: 'metrics.comparison.kpi.pacientesNuevos', apiField: 'pacientes.nuevos', format: 'integer', inSelector: true },
  { key: 'attendanceRate', labelKey: 'metrics.comparison.kpi.asistencia', apiField: 'turnos.attendanceRate', format: 'percent', inSelector: true },
  { key: 'cajaNeto', labelKey: 'metrics.comparison.kpi.neto', apiField: 'caja.neto', format: 'currency', inSelector: false },
  { key: 'pacientesActivos', labelKey: 'metrics.comparison.kpi.pacientesActivos', apiField: 'pacientes.activos', format: 'integer', inSelector: false },
];

export const COMPARISON_SELECTOR_METRICS: ComparisonMetricKey[] = COMPARISON_METRICS_CATALOG.filter(
  (item) => item.inSelector
).map((item) => item.key);

const BY_KEY = new Map(COMPARISON_METRICS_CATALOG.map((item) => [item.key, item]));

export function comparisonMetricDef(key: ComparisonMetricKey): ComparisonMetricDef | undefined {
  return BY_KEY.get(key);
}

export function comparisonMetricLabelKey(key: ComparisonMetricKey): string {
  return BY_KEY.get(key)?.labelKey ?? 'metrics.comparison.kpi.ingresos';
}
